import { useEffect, useMemo, useState } from 'react';

import { Divider, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { getUserL2Access } from 'Deer/helpers/getUserAccess';
import { useOpenCompetencyDrawer } from 'components/Develop/helpers/useOpenCompetencyDrawer';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import { CFProgressBar } from 'screens/ContinuousFeedback/components/CFProgressBar';
import { LevelUpRoleOption } from 'screens/Levelup/components/LevelUpFilters';
import { CompetencyProgressCell } from 'screens/Levelup/components/common';
import { useLevelUpProfilePageContext } from 'screens/Profile/competencySection/context';
import useLevelUpSidePanelContext from 'screens/oneOnOne/levelUp/competencySidePanel/context';

import { LevelUpIcon, OpenSidePanel } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { Select } from 'uiToolkitV1/Select';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { useSummaryPagination } from '../../hooks/useSummaryPagination';
import { ProfileSummaryCardTemplate } from '../ProfileSummaryCardTemplate';
import { useMySummaryCompetency } from './hooks';

const RowContainer = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
  cursor: pointer;
`;
const ButtonContainer = styled('div')`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${RowContainer}:hover & {
    visibility: visible;
    display: flex;
  }
`;

const ViewDetailsButton = styled(Button)`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${RowContainer}:hover & {
    visibility: visible;
    display: flex;
  }
`;

const TimePeriod = styled(TextBodyMedium)`
  width: 102px;
  margin-left: auto;
  display: block;
  ${RowContainer}:hover & {
    display: none;
  }
`;

const RoleDropdown = ({ isLoading, reactSelectOptions = [], setSelectedRoleIndex, selectedRoleIndex }) => {
  const selectedOption = reactSelectOptions?.find(({ value }) => value === selectedRoleIndex);
  if (isLoading || reactSelectOptions?.length < 2) {
    return null;
  }
  return (
    <>
      {(!isLoading || reactSelectOptions?.length > 1) && (
        <Flex style={{ marginLeft: 'auto' }}>
          <Flex
            alignItems="center"
            sx={{
              '.select': {
                width: '100%',
              },
            }}
          >
            <TextBodyMedium sx={{ marginRight: '4px' }} bold color="text.g3">
              Current view:
            </TextBodyMedium>
            <div style={{ width: '12rem', zIndex: '5' }}>
              <Select
                options={reactSelectOptions}
                value={selectedOption}
                isDisabled={reactSelectOptions?.length < 2 || isLoading}
                handleChange={(newValue) => setSelectedRoleIndex(newValue.value)}
                components={{
                  // MenuList: ChangeFutureRoleMenu,
                  Option: LevelUpRoleOption,
                }}
              />
            </div>
          </Flex>
        </Flex>
      )}
    </>
  );
};

export const CompetenciesMySummaryRow = ({
  levelUpCompetencyId,
  competencyId,
  name,
  targetPeriodStart,
  progressPercentage,
  progress,
  handleOpenLevelUpSidePanel,
  levelUpId,
  showDivider,
  isLevelUpEnabled,
  openNonLevelUpCompetencySidePanel = () => {},
  progressDetails,
}) => {
  const { periodName } = targetPeriodStart ?? {};
  // const cursorType = competencyId && levelUpCompetencyId ? 'pointer' : 'default';
  return (
    <>
      <RowContainer
        onClick={() => {
          if (!isLevelUpEnabled) {
            openNonLevelUpCompetencySidePanel({ competencyId, name });
          }
          handleOpenLevelUpSidePanel({ levelUpCompetencyId, competencyId, levelUpId });
        }}
      >
        <Flex width="100%" flexDir="column">
          <Flex height="28px" alignItems="center">
            <TextBodyMedium color="text.g1" className="highlightTextOnHover">
              {name}
            </TextBodyMedium>
            <TimePeriod color="text.g2">{periodName}</TimePeriod>
            <ButtonContainer className="showOnHover">
              <ViewDetailsButton
                variant="rowButtonOutlined"
                StartIcon={OpenSidePanel}
                onClick={() => {
                  if (!isLevelUpEnabled) {
                    openNonLevelUpCompetencySidePanel({ competencyId, name });
                  }
                  handleOpenLevelUpSidePanel({ levelUpCompetencyId, competencyId, levelUpId });
                }}
              >
                View Details
              </ViewDetailsButton>
            </ButtonContainer>
          </Flex>
          <div style={{ width: '230px', marginTop: '10px' }}>
            <CFProgressBar value={progressDetails} />
          </div>
        </Flex>
      </RowContainer>
      {showDivider && <Divider color="neutral.CFD7DF" />}
    </>
  );
};

const CompetenciesProfileSummaryCard = ({ employeeId }) => {
  const {
    user: { id },
  } = useAuthenticatedUser();

  const { openCompetencyPanel } = useOpenCompetencyDrawer();

  const { openLevelUpSidePanel } = useLevelUpSidePanelContext();
  const {
    state: { reporteeId, checkInInstanceId, checkInParentId, filterEmpIds, isLoggedInEmployeeManager, levelUpDetails },
  } = useLevelUpProfilePageContext();

  const { hasMore, size, setDataLength, setPage } = useSummaryPagination();
  const { data, isLoading, isPreviousData, isFetching, isError } = useMySummaryCompetency({
    limit: size,
    offset: 0,
    employeeId: employeeId ?? id,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { isLevelUpEnabled } = data ?? {};

  const jobCompetencies = [
    {
      competencies: data?.competencies,
    },
  ];

  const hasTargetRole = jobCompetencies?.length > 1;
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(0); // <- 0 in current role and 1 is target role

  useEffect(() => {
    setSelectedRoleIndex(hasTargetRole ? 1 : 0);
  }, [hasTargetRole]);

  const handleOpenLevelUpSidePanel = ({ competencyId, levelUpId, levelUpCompetencyId }) => {
    openLevelUpSidePanel({
      reporteeId,
      competencyId,
      levelUpId,
      levelUpCompetencyId,
      isLoggedInEmployeeManager,
      checkInInstanceId,
      checkInParentId,
      filterEmpIds,
    });
  };

  const openNonLevelUpCompetencySidePanel = ({ competencyId, name }) => {
    openCompetencyPanel({
      tagId: competencyId,
      tagTitle: name,
      employeeId: employeeId ?? id,
    });
  };

  useEffect(() => {
    if (!isLoading) {
      if (Array.isArray(jobCompetencies)) {
        setDataLength(jobCompetencies[selectedRoleIndex]?.competencies?.length);
      }
    }
  }, [isLoading, data]);

  // Develop is ON but progressDetails is null (i.e.logged in person is peer of the employee)
  const canAccessCompetencyProgress = useMemo(
    () => jobCompetencies[selectedRoleIndex]?.competencies?.[0]?.progressDetails !== null,
    [jobCompetencies, selectedRoleIndex]
  );

  const { hasIDPAccess } = getUserL2Access();

  // Competency section won't be visible when -
  // Develop if OFF but Feedback is ON
  // Both Develop and Feedback is OFF
  const hideCompetencySection = !hasIDPAccess;

  if (!canAccessCompetencyProgress || hideCompetencySection) {
    return null;
  }

  if (isLoading) {
    return (
      <ProfileSummaryCardTemplate Icon={LevelUpIcon} heading="Competencies" showViewAllButton={false}>
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </ProfileSummaryCardTemplate>
    );
  }

  if (!jobCompetencies) {
    return null;
  }

  if (!jobCompetencies[selectedRoleIndex]?.competencies?.length) {
    return (
      <EmptySummaryCard
        Icon={LevelUpIcon}
        heading="Competencies"
        description="There were no competencies found for this role"
        // illustrationSrc={NoDataImg}
      />
    );
  }

  return (
    <ProfileSummaryCardTemplate
      Icon={LevelUpIcon}
      heading="Competencies"
      showViewAllButton={hasMore}
      onClickViewAll={() => {
        setPage((prev) => prev + 1);
      }}
      viewAllBtnDisabled={isPreviousData || !hasMore}
    >
      {jobCompetencies[selectedRoleIndex]?.competencies?.map(
        (
          { levelUpCompetencyId, competencyId, name, targetPeriodStart, progressPercentage, progress, progressDetails },
          index
        ) => (
          <CompetenciesMySummaryRow
            levelUpCompetencyId={levelUpCompetencyId}
            competencyId={competencyId}
            name={name}
            targetPeriodStart={targetPeriodStart}
            progressPercentage={progressPercentage}
            progress={progress}
            key={`${competencyId}-${index}-competency-row`}
            handleOpenLevelUpSidePanel={handleOpenLevelUpSidePanel}
            levelUpId={jobCompetencies[selectedRoleIndex]?.levelUpId}
            showDivider
            isLevelUpEnabled={isLevelUpEnabled}
            openNonLevelUpCompetencySidePanel={openNonLevelUpCompetencySidePanel}
            progressDetails={progressDetails}
          />
        )
      )}
      {isFetching ? <SkeletonLoader /> : null}
    </ProfileSummaryCardTemplate>
  );
};

export { CompetenciesProfileSummaryCard };
